import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  AccordionProps,
  Tooltip,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Pencil, PencilOff } from "lucide-react";
import { colorSystem } from "../../../theme";
import { accordionStyles } from "./accordionStyles";
import { StackedMicroRing } from "../StackedMicroRing";
import { thousandSeparator } from "../utils/thousandSeparator";

const useStyles = makeStyles((theme) => ({
  summary: {
    maxHeight: 60,
    padding: theme.spacing(3),

    [theme.breakpoints.down(300)]: {
      paddingInline: theme.spacing(1),
    },
  },

  summaryContent: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },

  details: {
    paddingInline: theme.spacing(3),
    paddingBottom: theme.spacing(3),

    [theme.breakpoints.down(300)]: {
      paddingInline: theme.spacing(1),
    },
  },

  title: {
    fontWeight: 700,
  },

  titleContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    alignItems: "center",
  },

  pencil: {
    width: 20,
    height: 20,
    color: colorSystem.gray,
  },

  caloriesSum: {
    fontWeight: 500,
  },

  caloriesTarget: {
    fontWeight: 500,
    fontSize: 13,
    color: theme.palette.grey[500],
  },

  actionIcon: {
    color: theme.palette.primary.main,
    cursor: "pointer",
    "& svg": {
      width: 20,
      height: 20,
    },
  },
}));

export interface NutritionAccordionItemAction {
  name: string;
  onClick: () => void;
  icon: React.ReactNode;
}

export interface NutritionAccordionProps extends AccordionProps {
  title: string;
  showSummaryOnExpanded: boolean;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  children: React.ReactNode;
  targetCalories?: number;
  secondaryAction?: NutritionAccordionItemAction;
  pencilOff?: boolean;
}

const NutritionAccordion = React.forwardRef(
  (props: NutritionAccordionProps, ref) => {
    const {
      title,
      showSummaryOnExpanded,
      calories,
      protein,
      carbs,
      fat,
      children,
      targetCalories,
      secondaryAction,
      pencilOff = false,
      ...other
    } = props;
    const s = useStyles();
    const a = accordionStyles();

    const { expanded } = other;

    const PencilIcon = expanded ? PencilOff : Pencil;

    const showSummary = !expanded || showSummaryOnExpanded;

    const proteinPercent = Math.round((protein / calories) * 100);
    const carbsPercent = Math.round((carbs / calories) * 100);
    const fatPercent = Math.round((fat / calories) * 100);

    const hasTargetCalories =
      targetCalories !== undefined && targetCalories !== null;

    return (
      <Accordion {...other} className={a.accordionRoot}>
        {showSummary && (
          <AccordionSummary expandIcon={undefined} className={s.summary}>
            <Box className={s.summaryContent}>
              <Box className={s.titleContainer}>
                <Typography component="span" className={s.title}>
                  {title}
                </Typography>
                {(!expanded || pencilOff) && (
                  <PencilIcon className={s.pencil} />
                )}
              </Box>
              <Box>
                {!expanded ? (
                  <Box className={s.titleContainer}>
                    <Typography component="span" className={s.caloriesSum}>
                      {thousandSeparator(Math.round(calories))}
                      {hasTargetCalories ? "" : " kcal"}
                    </Typography>
                    {hasTargetCalories && (
                      <>
                        <Typography
                          component="span"
                          className={s.caloriesTarget}
                        >
                          / {thousandSeparator(Math.round(targetCalories))} kcal
                        </Typography>
                      </>
                    )}
                    <span>
                      <StackedMicroRing
                        values={[proteinPercent, carbsPercent, fatPercent]}
                        colors={[
                          colorSystem.nutritionProteins,
                          colorSystem.nutritionCarbs,
                          colorSystem.nutritionFats,
                        ]}
                      />
                    </span>
                  </Box>
                ) : secondaryAction ? (
                  <Tooltip title={secondaryAction.name}>
                    <span
                      onClick={secondaryAction.onClick}
                      className={s.actionIcon}
                    >
                      {secondaryAction.icon}
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            </Box>
          </AccordionSummary>
        )}
        <AccordionDetails
          className={s.details}
          sx={{ paddingTop: showSummary ? 0 : 3 }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default NutritionAccordion;
